import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Alert,
  Badge,
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Spinner,
  Table,
} from "reactstrap";

import usePlanFeatures from "../../hooks/usePlanFeatures";
import { formatMoneyFromCents } from "../../helpers/money";
import {
  analyzeImport,
  confirmImportMapping,
  downloadImportErrors,
  downloadImportTemplate,
  DuplicateStrategy,
  executeImport,
  getImportSession,
  ImportAnalyzeData,
  ImportAvailableLayout,
  ImportEntityType,
  ImportSessionStatusData,
  ImportSourceLayout,
  undoImport,
} from "../../services/importService";

const ACCEPTED_EXTENSIONS = [".csv", ".txt", ".xlsx", ".xls"];

type Step = "upload" | "mapping" | "running" | "summary";

type SmartImportWizardProps = {
  isOpen: boolean;
  onClose: () => void;
  entityType: ImportEntityType;
  onCompleted?: () => void;
  /** After a successful clients import, open merge / duplicates review. */
  onReviewDuplicates?: () => void;
};

const isAcceptedFile = (file: File): boolean => {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const friendlyApiError = (error: unknown, fallback: string): string => {
  const err = error as { data?: string; message?: string };
  if (typeof err?.data === "string" && err.data.trim() !== "") {
    return err.data;
  }
  if (typeof err?.message === "string" && err.message.trim() !== "") {
    return err.message;
  }
  return fallback;
};

const triggerBlobDownload = (blob: Blob, filename: string): void => {
  if (!(blob instanceof Blob)) {
    throw new Error("Invalid download blob");
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const SmartImportWizard = ({
  isOpen,
  onClose,
  entityType,
  onCompleted,
  onReviewDuplicates,
}: SmartImportWizardProps) => {
  const { t } = useTranslation();
  const { can, isLoading: planLoading } = usePlanFeatures();
  const canImport = can("client_import");

  const [step, setStep] = useState<Step>("upload");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [analyzeData, setAnalyzeData] = useState<ImportAnalyzeData | null>(null);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [duplicateStrategy, setDuplicateStrategy] = useState<DuplicateStrategy>("skip");
  const [previewRows, setPreviewRows] = useState<Record<string, unknown>[]>([]);
  const [warnings, setWarnings] = useState<ImportAnalyzeData["warnings"]>([]);
  const [statusData, setStatusData] = useState<ImportSessionStatusData | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [currencyCode, setCurrencyCode] = useState("BRL");
  const [moneyLocale, setMoneyLocale] = useState("pt-BR");
  const [showColumnEditor, setShowColumnEditor] = useState(false);
  const [refreshingPreview, setRefreshingPreview] = useState(false);
  const [selectedLayoutOverride, setSelectedLayoutOverride] = useState<string>("");
  const [applyingLayout, setApplyingLayout] = useState(false);

  const destinationFields = useMemo(
    () => analyzeData?.destination_fields ?? [],
    [analyzeData?.destination_fields]
  );
  const headers = useMemo(
    () => analyzeData?.headers ?? [],
    [analyzeData?.headers]
  );
  const canExecute =
    (analyzeData?.total_rows ?? 0) > 0 && !confirming && !executing;

  const formatPreviewCell = (column: string, value: unknown): string => {
    if (
      (column === "price_cents" || column === "sale_price" || column === "cost_price_cents") &&
      value != null &&
      value !== ""
    ) {
      return formatMoneyFromCents(Number(value), moneyLocale, currencyCode);
    }
    if (column === "tags" && Array.isArray(value)) {
      return value.length > 0 ? value.join(", ") : "—";
    }
    if (column === "initial_stock" && value != null && value !== "") {
      return String(value);
    }
    if (column === "initial_stock_provided") {
      if (value === true || value === "true" || value === 1 || value === "1") {
        return t("imports.wizard.bool_true");
      }
      if (value === false || value === "false" || value === 0 || value === "0") {
        return t("imports.wizard.bool_false");
      }
    }
    if (
      column === "marketing_email_opt_out" ||
      column === "appointment_email_opt_out" ||
      column === "sms_marketing_opt_out" ||
      column === "allow_online_booking" ||
      column === "is_active"
    ) {
      if (value === true) {
        return t("imports.wizard.bool_yes");
      }
      if (value === false) {
        return t("imports.wizard.bool_no");
      }
    }
    return value == null || value === "" ? "—" : String(value);
  };

  const previewColumns = useMemo(() => {
    if (previewRows.length === 0) {
      return destinationFields;
    }
    return Object.keys(previewRows[0]).filter(
      (key) =>
        ![
          "email_suppressed_at",
          "country_code",
          "secondary_country_code",
          "price",
          "document_type",
          "phone_secondary",
        ].includes(key)
    );
  }, [previewRows, destinationFields]);

  const showServiceDescriptionHint = useMemo(() => {
    if (entityType !== "services") {
      return false;
    }

    const mappingHasDescription = Object.values(columnMapping).includes("description");
    const previewHasDescription = previewRows.some((row) => {
      const value = row.description;
      return typeof value === "string" && value.trim() !== "";
    });

    return mappingHasDescription || previewHasDescription;
  }, [columnMapping, entityType, previewRows]);

  const resetState = () => {
    setStep("upload");
    setSelectedFile(null);
    setFileInputKey((current) => current + 1);
    setAnalyzing(false);
    setConfirming(false);
    setExecuting(false);
    setAnalyzeData(null);
    setColumnMapping({});
    setDuplicateStrategy("skip");
    setPreviewRows([]);
    setWarnings([]);
    setStatusData(null);
    setUndoing(false);
    setCurrencyCode("BRL");
    setMoneyLocale("pt-BR");
    setShowColumnEditor(false);
    setRefreshingPreview(false);
    setSelectedLayoutOverride("");
    setApplyingLayout(false);
  };

  const handleClose = () => {
    if (analyzing || confirming || executing || step === "running") {
      return;
    }
    resetState();
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    resetState();
  }, [isOpen, entityType]);

  useEffect(() => {
    if (step !== "running" || !statusData?.session_id) {
      return;
    }

    if (statusData.status === "completed" || statusData.status === "failed" || statusData.status === "undone") {
      setStep("summary");
      if (statusData.status === "completed") {
        onCompleted?.();
      }
      return;
    }

    const timer = window.setInterval(async () => {
      try {
        const response = await getImportSession(statusData.session_id);
        setStatusData(response.data);
      } catch (error) {
        window.clearInterval(timer);
        toast.error(friendlyApiError(error, t("imports.wizard.poll_error")));
        setStep("summary");
      }
    }, 2000);

    return () => window.clearInterval(timer);
  }, [step, statusData, onCompleted, t]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) {
      setSelectedFile(null);
      return;
    }
    if (!isAcceptedFile(file)) {
      toast.error(t("imports.wizard.invalid_file"));
      setSelectedFile(null);
      setFileInputKey((current) => current + 1);
      return;
    }
    setSelectedFile(file);
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadImportTemplate(entityType);
      triggerBlobDownload(blob, `adatis-import-${entityType}.csv`);
    } catch (error) {
      toast.error(friendlyApiError(error, t("imports.wizard.template_error")));
    }
  };

  const handleAnalyze = async () => {
    if (!selectedFile) {
      toast.error(t("imports.wizard.no_file"));
      return;
    }

    setAnalyzing(true);
    try {
      const response = await analyzeImport(selectedFile, entityType);
      setAnalyzeData(response.data);
      setColumnMapping(response.data.suggested_mapping ?? {});
      setDuplicateStrategy(response.data.duplicate_strategy ?? "skip");
      setPreviewRows(response.data.preview_rows ?? []);
      setWarnings(response.data.warnings ?? []);
      setCurrencyCode(response.data.currency_code || "BRL");
      setMoneyLocale(response.data.locale || "pt-BR");
      setShowColumnEditor(Boolean(response.data.requires_manual_mapping));
      setSelectedLayoutOverride(response.data.source_layout || "");
      setStep("mapping");
    } catch (error) {
      toast.error(friendlyApiError(error, t("imports.wizard.analyze_error")));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleApplyLayoutOverride = async () => {
    if (!analyzeData || !selectedLayoutOverride) {
      return;
    }

    setApplyingLayout(true);
    try {
      const response = await confirmImportMapping(
        analyzeData.session_id,
        null,
        duplicateStrategy,
        selectedLayoutOverride as ImportSourceLayout
      );
      setColumnMapping(response.data.column_mapping ?? {});
      setPreviewRows(response.data.preview_rows ?? []);
      setWarnings(response.data.error_summary?.warnings ?? []);
      setAnalyzeData((current) =>
        current
          ? {
              ...current,
              source_layout: (response.data.source_layout ??
                selectedLayoutOverride) as ImportSourceLayout,
              layout_banner: response.data.layout_banner ?? current.layout_banner,
              suggested_mapping: response.data.column_mapping ?? current.suggested_mapping,
              requires_manual_mapping: Boolean(response.data.requires_manual_mapping),
              available_layouts:
                response.data.available_layouts ?? current.available_layouts,
              preview_rows: response.data.preview_rows ?? current.preview_rows,
            }
          : current
      );
      setShowColumnEditor(false);
      toast.success(t("imports.wizard.layout_applied"));
    } catch (error) {
      toast.error(friendlyApiError(error, t("imports.wizard.layout_apply_error")));
    } finally {
      setApplyingLayout(false);
    }
  };

  const handleRefreshPreview = async () => {
    if (!analyzeData) {
      return;
    }

    setRefreshingPreview(true);
    try {
      const response = await confirmImportMapping(
        analyzeData.session_id,
        columnMapping,
        duplicateStrategy
      );
      setPreviewRows(response.data.preview_rows ?? []);
      setWarnings(response.data.error_summary?.warnings ?? warnings);
      toast.success(t("imports.wizard.preview_refreshed"));
    } catch (error) {
      toast.error(friendlyApiError(error, t("imports.wizard.preview_refresh_error")));
    } finally {
      setRefreshingPreview(false);
    }
  };

  const handleConfirmAndExecute = async () => {
    if (!analyzeData) {
      return;
    }

    if ((analyzeData.total_rows ?? 0) <= 0) {
      toast.error(t("imports.wizard.no_rows"));
      return;
    }

    const required = analyzeData.required_fields ?? [];
    const mappedValues = Object.values(columnMapping).filter(Boolean);
    const missingRequired = required.filter((field) => !mappedValues.includes(field));

    if (missingRequired.length > 0) {
      toast.error(
        t("imports.wizard.missing_required", {
          fields: missingRequired
            .map((field) =>
              t(`imports.fields.${entityType}.${field}`, { defaultValue: field })
            )
            .join(", "),
        })
      );
      return;
    }

    setConfirming(true);
    try {
      const mappingPayload = showColumnEditor ? columnMapping : null;
      await confirmImportMapping(
        analyzeData.session_id,
        mappingPayload,
        duplicateStrategy
      );
      setExecuting(true);
      const executed = await executeImport(analyzeData.session_id);
      setStatusData(executed.data);
      setStep("running");
      toast.success(t("imports.wizard.execute_started"));
    } catch (error) {
      toast.error(friendlyApiError(error, t("imports.wizard.execute_error")));
    } finally {
      setConfirming(false);
      setExecuting(false);
    }
  };

  const handleDownloadErrors = async () => {
    if (!statusData?.session_id) {
      return;
    }
    try {
      const blob = await downloadImportErrors(statusData.session_id);
      triggerBlobDownload(blob, `import-errors-${statusData.session_id}.csv`);
    } catch (error) {
      toast.error(friendlyApiError(error, t("imports.wizard.errors_download_error")));
    }
  };

  const handleUndo = async () => {
    if (!statusData?.session_id || !statusData.can_undo) {
      return;
    }
    setUndoing(true);
    try {
      const response = await undoImport(statusData.session_id);
      setStatusData(response.data);
      toast.success(t("imports.wizard.undo_success"));
      onCompleted?.();
    } catch (error) {
      toast.error(friendlyApiError(error, t("imports.wizard.undo_error")));
    } finally {
      setUndoing(false);
    }
  };

  const title = t(`imports.wizard.title.${entityType}`);
  const progressPercent = statusData?.progress_percent ?? 0;
  const layoutDetected =
    analyzeData?.source_layout && analyzeData.source_layout !== "unknown";

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg">
      <ModalHeader toggle={step === "running" ? undefined : handleClose}>{title}</ModalHeader>
      <ModalBody>
        {!planLoading && !canImport ? (
          <Alert color="warning" className="mb-0">
            <p className="mb-2">{t("imports.wizard.feature_locked")}</p>
            <Link to="/settings/subscription" className="btn btn-sm btn-warning">
              {t("imports.wizard.upsell_cta")}
            </Link>
          </Alert>
        ) : null}

        {canImport && step === "upload" ? (
          <div>
            <p className="text-muted">{t(`imports.wizard.instructions.${entityType}`)}</p>
            <Alert color="light" className="border mb-3">
              <i className="ri-lightbulb-line align-middle me-1 text-warning" />
              {t("imports.wizard.concierge_tip")}
            </Alert>
            <div className="mb-3">
              <Button color="soft-secondary" size="sm" onClick={handleDownloadTemplate}>
                <i className="ri-download-2-line align-bottom me-1" />
                {t("imports.wizard.download_template")}
              </Button>
            </div>
            <div className="mb-2">
              <Label htmlFor="smart-import-file">{t("imports.wizard.file_label")}</Label>
              <Input
                key={fileInputKey}
                id="smart-import-file"
                type="file"
                accept=".csv,.txt,.xlsx,.xls,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleFileChange}
              />
              <small className="text-muted d-block mt-1">{t("imports.wizard.file_hint")}</small>
            </div>
            {selectedFile ? (
              <Badge color="light" className="text-dark">
                {selectedFile.name}
              </Badge>
            ) : null}
          </div>
        ) : null}

        {canImport && step === "mapping" && analyzeData ? (
          <div>
            {analyzeData.layout_banner ? (
              <Alert color="success" className="mb-3">
                {analyzeData.layout_banner}
              </Alert>
            ) : (
              <Alert color="info" className="mb-3">
                {t("imports.wizard.mapping_hint", {
                  total: analyzeData.total_rows,
                  confidence: Math.round((analyzeData.confidence ?? 0) * 100),
                })}
              </Alert>
            )}

            {!layoutDetected ? (
              <Alert color="warning" className="mb-3">
                {t("imports.wizard.unknown_layout_hint")}
              </Alert>
            ) : null}

            {(analyzeData.available_layouts?.length ?? 0) > 0 ? (
              <div className="mb-3">
                <Label htmlFor="source-layout-override">
                  {t("imports.wizard.use_other_layout")}
                </Label>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <Input
                    id="source-layout-override"
                    type="select"
                    className="w-auto"
                    value={selectedLayoutOverride}
                    onChange={(event) => setSelectedLayoutOverride(event.target.value)}
                    disabled={applyingLayout || confirming || executing}
                  >
                    {(analyzeData.available_layouts as ImportAvailableLayout[]).map(
                      (layout) => (
                        <option key={layout.key} value={layout.key}>
                          {layout.label}
                        </option>
                      )
                    )}
                  </Input>
                  <Button
                    color="soft-primary"
                    size="sm"
                    onClick={handleApplyLayoutOverride}
                    disabled={
                      applyingLayout ||
                      confirming ||
                      executing ||
                      !selectedLayoutOverride ||
                      selectedLayoutOverride === analyzeData.source_layout
                    }
                  >
                    {applyingLayout ? <Spinner size="sm" className="me-1" /> : null}
                    {t("imports.wizard.apply_layout")}
                  </Button>
                </div>
                <small className="text-muted d-block mt-1">
                  {t("imports.wizard.use_other_layout_hint")}
                </small>
              </div>
            ) : null}

            {showColumnEditor ? (
              <div className="mb-3">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                  <small className="text-muted mb-0">{t("imports.wizard.adjust_columns_hint")}</small>
                  <Button
                    color="soft-primary"
                    size="sm"
                    onClick={handleRefreshPreview}
                    disabled={refreshingPreview || confirming || executing}
                  >
                    {refreshingPreview ? <Spinner size="sm" className="me-1" /> : null}
                    {t("imports.wizard.refresh_preview")}
                  </Button>
                </div>
                <div className="table-responsive">
                  <Table size="sm" bordered className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>{t("imports.wizard.source_column")}</th>
                        <th>{t("imports.wizard.destination_field")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {headers.map((header) => (
                        <tr key={header}>
                          <td>{header}</td>
                          <td>
                            <Input
                              type="select"
                              value={columnMapping[header] ?? ""}
                              onChange={(event) =>
                                setColumnMapping((current) => ({
                                  ...current,
                                  [header]: event.target.value,
                                }))
                              }
                            >
                              <option value="">{t("imports.wizard.ignore_column")}</option>
                              {destinationFields.map((field) => (
                                <option key={field} value={field}>
                                  {t(`imports.fields.${entityType}.${field}`, {
                                    defaultValue: field,
                                  })}
                                </option>
                              ))}
                            </Input>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            ) : null}

            {entityType === "clients" || entityType === "products" ? (
              <div className="mb-3">
                <Label htmlFor="duplicate-strategy">{t("imports.wizard.duplicate_strategy")}</Label>
                <Input
                  id="duplicate-strategy"
                  type="select"
                  value={duplicateStrategy}
                  onChange={(event) =>
                    setDuplicateStrategy(event.target.value as DuplicateStrategy)
                  }
                >
                  <option value="skip">{t("imports.wizard.duplicate.skip")}</option>
                  <option value="update">{t("imports.wizard.duplicate.update")}</option>
                  <option value="create">{t("imports.wizard.duplicate.create")}</option>
                </Input>
              </div>
            ) : null}

            {warnings.length > 0 ? (
              <Alert color="warning">
                <strong className="d-block mb-1">{t("imports.wizard.warnings_title")}</strong>
                <ul className="mb-0 ps-3">
                  {warnings.slice(0, 5).map((warning) => (
                    <li key={`${warning.row}-${warning.message}`}>{warning.message}</li>
                  ))}
                </ul>
                {warnings.length > 5 ? (
                  <small className="d-block mt-2">
                    {t("imports.wizard.warnings_more", { count: warnings.length - 5 })}
                  </small>
                ) : null}
              </Alert>
            ) : null}

            {showServiceDescriptionHint ? (
              <Alert color="info" className="mb-3">
                <i className="ri-information-line align-middle me-1" />
                {t("imports.wizard.services_description_hint")}
              </Alert>
            ) : null}

            <h6 className="mb-2">{t("imports.wizard.preview_title")}</h6>
            {(analyzeData.total_rows ?? 0) <= 0 ? (
              <Alert color="warning" className="mb-0">
                {t("imports.wizard.no_rows")}
              </Alert>
            ) : previewRows.length === 0 ? (
              <p className="text-muted mb-0">{t("imports.wizard.preview_empty")}</p>
            ) : (
              <div className="table-responsive">
                <Table size="sm" className="mb-0">
                  <thead>
                    <tr>
                      {previewColumns.map((column) => (
                        <th key={column}>
                          {t(`imports.fields.${entityType}.${column}`, {
                            defaultValue: column,
                          })}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, index) => (
                      <tr key={`preview-${index}`}>
                        {previewColumns.map((column) => (
                          <td key={column}>{formatPreviewCell(column, row[column])}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        ) : null}

        {canImport && step === "running" ? (
          <div className="text-center py-4">
            <Spinner color="primary" className="mb-3" />
            <p className="mb-1">{t("imports.wizard.running_title")}</p>
            <p className="text-muted mb-3">{t("imports.wizard.running_hint")}</p>
            <div className="px-4">
              <Progress value={progressPercent} className="mb-2" />
              <small className="text-muted">
                {t("imports.wizard.progress_label", {
                  percent: progressPercent,
                  processed: statusData?.processed_rows ?? 0,
                  total: statusData?.total_rows ?? 0,
                })}
              </small>
            </div>
          </div>
        ) : null}

        {canImport && step === "summary" && statusData ? (
          <div>
            {statusData.status === "undone" ? (
              <Alert color="info">{t("imports.wizard.summary_undone")}</Alert>
            ) : statusData.status === "failed" ? (
              <Alert color="danger">{t("imports.wizard.summary_failed")}</Alert>
            ) : (
              <Alert color="success">{t("imports.wizard.summary_success")}</Alert>
            )}
            <ul className="list-unstyled mb-3">
              <li>
                {t("imports.wizard.summary_total", { count: statusData.total_rows })}
              </li>
              <li>
                {t("imports.wizard.summary_success_count", {
                  count: statusData.success_count,
                })}
              </li>
              <li>
                {t("imports.wizard.summary_failure_count", {
                  count: statusData.failure_count,
                })}
              </li>
              {(statusData.error_summary?.skipped_count ?? 0) > 0 ? (
                <li>
                  {t("imports.wizard.summary_skipped_count", {
                    count: statusData.error_summary?.skipped_count ?? 0,
                  })}
                </li>
              ) : null}
            </ul>
            {entityType === "clients" &&
            statusData.status === "completed" &&
            (statusData.success_count ?? 0) > 0 ? (
              <Alert color="light" className="border mb-3">
                <p className="mb-2">{t("imports.wizard.review_duplicates_hint")}</p>
                {onReviewDuplicates ? (
                  <Button
                    color="soft-secondary"
                    size="sm"
                    onClick={() => {
                      onReviewDuplicates();
                      resetState();
                      onClose();
                    }}
                  >
                    <i className="ri-merge-cells-horizontal align-bottom me-1" />
                    {t("imports.wizard.review_duplicates_cta")}
                  </Button>
                ) : null}
              </Alert>
            ) : null}
            <div className="d-flex flex-wrap gap-2">
              {statusData.errors_download_available ? (
                <Button color="soft-danger" size="sm" onClick={handleDownloadErrors}>
                  <i className="ri-file-download-line align-bottom me-1" />
                  {t("imports.wizard.download_errors")}
                </Button>
              ) : null}
              {statusData.can_undo ? (
                <Button color="soft-warning" size="sm" onClick={handleUndo} disabled={undoing}>
                  {undoing ? <Spinner size="sm" className="me-1" /> : null}
                  {t("imports.wizard.undo")}
                </Button>
              ) : null}
            </div>
            {statusData.can_undo ? (
              <small className="text-muted d-block mt-2">{t("imports.wizard.undo_hint")}</small>
            ) : null}
          </div>
        ) : null}
      </ModalBody>

      <ModalFooter>
        {canImport && step === "upload" ? (
          <>
            <Button color="light" onClick={handleClose} disabled={analyzing}>
              {t("imports.wizard.cancel")}
            </Button>
            <Button color="primary" onClick={handleAnalyze} disabled={analyzing || !selectedFile}>
              {analyzing ? <Spinner size="sm" className="me-1" /> : null}
              {t("imports.wizard.analyze")}
            </Button>
          </>
        ) : null}

        {canImport && step === "mapping" && analyzeData ? (
          <>
            <Button
              color="light"
              onClick={() => setStep("upload")}
              disabled={confirming || executing}
            >
              {t("imports.wizard.back")}
            </Button>
            {!showColumnEditor ? (
              <Button
                color="soft-secondary"
                onClick={() => setShowColumnEditor(true)}
                disabled={confirming || executing}
              >
                {t("imports.wizard.adjust_columns")}
              </Button>
            ) : null}
            <Button
              color="primary"
              onClick={handleConfirmAndExecute}
              disabled={!canExecute}
            >
              {confirming || executing ? <Spinner size="sm" className="me-1" /> : null}
              {t("imports.wizard.confirm_execute_count", {
                count: analyzeData.total_rows,
                defaultValue: t("imports.wizard.confirm_execute"),
              })}
            </Button>
          </>
        ) : null}

        {canImport && step === "summary" ? (
          <Button
            color="primary"
            onClick={() => {
              resetState();
              onClose();
            }}
          >
            {t("imports.wizard.close")}
          </Button>
        ) : null}

        {!canImport ? (
          <Button color="light" onClick={handleClose}>
            {t("imports.wizard.close")}
          </Button>
        ) : null}
      </ModalFooter>
    </Modal>
  );
};

export default SmartImportWizard;
